/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React context
import { useSoftUIController, setLayout, setMiniSidenav } from "context";

function DashboardLayout({ children }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav } = controller;
  const { pathname } = useLocation();

  useEffect(() => {
    setLayout(dispatch, "dashboard");
  }, [dispatch, pathname]);

  // Chatbot Widget Injection
  useEffect(() => {
    // 1. Define Settings
    // pointer-events: containeren er en usynlig boks på 376×100 px nederst til
    // høyre med z-index 10000, og slukte klikk på alt under — bl.a. «Neste» i
    // e-postmal-listen. Nå er det bare boblen og det åpne vinduet som tar klikk.
    window.chatbotSettings = {
      "logo": "/placeholder.svg?height=40&width=40",
      "primary_color": "#0055aa",
      "secondary_color": "#f0f9ff",
      "position": "bottom-right",
      "size": "medium",
      "border_radius": "12px",
      "animation_style": "bounce",
      "bubble_style": "rounded",
      "font_family": "Inter, sans-serif",
      "show_avatar": true,
      "chat_title": "Chat Support",
      "welcome_message": "Hello! How can I help you today?",
      "placeholder": "Type your message...",
      "custom_css": ".chat-widget-container {\n    position: fixed;\n    z-index: 10000;\n    font-family: Inter, sans-serif;\n    height: 100px;\n    bottom: 0 !important;\n    right: 20px !important;\n    top: auto !important;\n  display: flex;\n  flex-direction: row-reverse;\n  pointer-events: none;\n} .chat-widget-container > * { pointer-events: auto; } .chat-window.hidden { pointer-events: none; } .chat-window.visible {height:  420px !important; transform: scale(1) translateY(-430px);}"
    };

    // 2. Load Script
    const script = document.createElement("script");
    script.src = "https://portal.orcube.com/assets/js/chat-widget-enhanced.js?customer_id=7&api_key=004fa73bd21e61abffb774e1a011c1c66e89ff282673aaf8efa8750160d1dbab";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Optional: Cleanup global object if needed
      // delete window.chatbotSettings; 
    };
  }, []);

  return (
    <SoftBox
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        p: 3,
        position: "relative",
        overflow: "hidden", // Remove scrollbars
        [breakpoints.up("xl")]: {
          marginLeft: miniSidenav ? pxToRem(120) : pxToRem(274),
          transition: transitions.create(["margin-left", "margin-right"], {
            easing: transitions.easing.easeInOut,
            duration: transitions.duration.standard,
          }),
        },
      })}
    >
      {/* ✅ Mobile/Tablet Open Button (shows ONLY when sidenav is hidden) */}
      {miniSidenav && (
        <SoftBox
          position="fixed"
          top={12}
          left={12}
          zIndex={3000}
          display={{ xs: "flex", sm: "flex", md: "flex", lg: "none" }}
        >
          <IconButton
            onClick={() => setMiniSidenav(dispatch, false)}
            sx={{
              backgroundColor: "#fff",
              boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
              borderRadius: "10px",
            }}
          >
            <Icon>menu</Icon>
          </IconButton>
        </SoftBox>
      )}

      {children}

      {/* Chatbot Widget Container */}
      <div id="my-chat-widget" style={{ display: "flex", alignItems: "flex-end" }}>
        <div className="chat-messages" id="chat-messages"></div>

        <div className="chat-input-area">
          <textarea className="chat-input" id="chat-input" placeholder="Ask a question..." rows={1}></textarea>
          <button className="chat-send-btn" id="chat-send">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
            </svg>
          </button>
        </div>
      </div>
    </SoftBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
